import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { getClientEmail } from '../utils/getClientEmail';
import { openRazorpayOrderCheckout } from '../subscriptionAdmin/razorpay.js';
import ContractCheckoutView from './ContractCheckoutView.js';
import SubscriptionPaymentBanner from './SubscriptionPaymentBanner.js';
import {
  USER_APP_ROBOTS_PATH,
  USER_CONTRACT_CHECKOUT_PATH,
  USER_SUBSCRIPTION_PLANS_PATH,
} from './paths.js';
import {
  confirmSubscriptionPlanPayment,
  fetchSubscriptionCatalogPlans,
  fetchSubscriptionStatus,
  selectSubscriptionPlan,
} from '../services/userSubscriptionApi.js';
import {
  PAYMENT_FLOW_CONTRACT_PENDING_ADMIN,
  PAYMENT_FLOW_SUBSCRIPTION_ACTIVE,
  isContractCheckoutPaymentContext,
  resolvePathFromPaymentContext,
  webPathFromAppRedirectRoute,
} from '../utils/clientAppRedirect.js';
import theme from '../UserDashboard/theme.js';

const accent = theme.colors.brandDark;

function computeFinalPrice(basePrice, discountPercent) {
  const base = Number(basePrice) || 0;
  const percent = Number(discountPercent) || 0;
  return Math.round(base - (base * percent) / 100);
}

function formatMoney(amount, currency = 'INR') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency || 'INR'} ${amount}`;
  }
}

function PlanDetail({ label, value }) {
  return (
    <Box sx={styles.detailRow}>
      <Typography sx={styles.detailLabel}>{label}</Typography>
      <Typography sx={styles.detailValue}>{value}</Typography>
    </Box>
  );
}

/** Detail rows with real catalog values only (avoids empty "—" cells). */
function getPlanDisplayFields(plan) {
  const rows = [];
  const hours = plan.hoursPerMonth ?? plan.benefits?.hoursPerMonth;
  if (hours != null && hours !== '') {
    rows.push({ label: 'UV hours / month', value: String(hours) });
  }

  const sessions = plan.benefits?.sessionsPerMonth;
  if (sessions > 0) {
    rows.push({ label: 'Sessions / month', value: String(sessions) });
  }

  const hoursPerSession = plan.benefits?.hoursPerSession;
  if (hoursPerSession > 0) {
    rows.push({ label: 'Hours / session', value: String(hoursPerSession) });
  }

  if (plan.validityMonths != null && plan.validityMonths > 0) {
    rows.push({
      label: 'Agreement term',
      value: `${plan.validityMonths} month${plan.validityMonths === 1 ? '' : 's'}`,
    });
  } else if (plan.restrictions?.validityDays) {
    rows.push({
      label: 'Validity',
      value: `${plan.restrictions.validityDays} days`,
    });
  }

  if (plan.freeMonths > 0) {
    rows.push({
      label: 'Free months',
      value: String(plan.freeMonths),
    });
  }

  if (plan.autoPayMonths != null && plan.autoPayMonths > 0) {
    rows.push({
      label: 'Paid billing months',
      value: String(plan.autoPayMonths),
    });
  }

  const autoPayOn =
    plan.billing?.autoPay?.enabled || plan.isAutoPayRequired;
  rows.push({
    label: 'Auto-pay',
    value: autoPayOn
      ? `Yes (${plan.billing?.autoPay?.billingCycle || 'monthly'})`
      : 'Not required',
  });

  if (plan.benefits?.unlimitedAccess) {
    rows.push({ label: 'Unlimited access', value: 'Yes' });
  }

  if (plan.restrictions?.allowsAddOns) {
    rows.push({ label: 'Add-ons', value: 'Available' });
  }

  return rows;
}

function PlanCard({ plan, onBuyPlan, buying, purchaseDisabled = false }) {
  const currency = plan.pricing?.currency || 'INR';
  const basePrice = plan.pricing?.basePrice;
  const discountPercent =
    plan.pricing?.discount?.percent ?? plan.pricing?.discountPercent ?? 0;
  const finalPrice =
    plan.pricing?.discount?.finalPrice ??
    plan.pricing?.finalPrice ??
    computeFinalPrice(basePrice, discountPercent);
  const features = Array.isArray(plan.benefits?.features) ? plan.benefits.features : [];
  const billingCycle = plan.billing?.autoPay?.billingCycle || 'monthly';
  const status = String(plan.metadata?.status || 'active').toLowerCase();
  const isActive = status === 'active' || status === '';
  const detailRows = getPlanDisplayFields(plan);

  return (
    <Paper elevation={0} sx={styles.planCard}>
      <Box sx={styles.planHeader}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={styles.planName}>{plan.planName}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {plan.planType ? (
              <Chip
                label={plan.planType}
                size="small"
                sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
              />
            ) : null}
            {plan.hoursPerMonth != null ? (
              <Chip
                label={`${plan.hoursPerMonth} hrs/mo`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: 11, height: 22, borderColor: accent, color: accent }}
              />
            ) : null}
            {plan.freeMonths > 0 ? (
              <Chip
                label={`${plan.freeMonths} free mo`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: theme.colors.accentSuccess,
                }}
              />
            ) : null}
          </Box>
          {plan.description ? (
            <Typography sx={styles.planDesc}>{plan.description}</Typography>
          ) : null}
        </Box>
        <Box sx={styles.priceBlock}>
          {discountPercent > 0 && basePrice != null ? (
            <Typography sx={styles.basePriceStrike}>{formatMoney(basePrice, currency)}</Typography>
          ) : null}
          <Typography sx={styles.finalPrice}>{formatMoney(finalPrice, currency)}</Typography>
          {discountPercent > 0 ? (
            <Typography sx={styles.discountNote}>{discountPercent}% off</Typography>
          ) : null}
          <Typography sx={styles.billingNote}>per {billingCycle}</Typography>
        </Box>
      </Box>

      {detailRows.length > 0 ? (
        <Grid container spacing={1.5} sx={{ mt: 2 }}>
          {detailRows.map((row) => (
            <Grid item xs={6} sm={4} key={row.label}>
              <PlanDetail label={row.label} value={row.value} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      {features.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={styles.featuresTitle}>Included</Typography>
          <Box sx={styles.featureList}>
            {features.map((f) => (
              <Box key={f} sx={styles.featureItem}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: theme.colors.accentSuccess }} />
                <Typography sx={styles.featureText}>{f}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box sx={styles.buyRow}>
        <Button
          variant="contained"
          disabled={!isActive || buying || purchaseDisabled}
          onClick={() => onBuyPlan(plan)}
          startIcon={buying ? <CircularProgress size={18} color="inherit" /> : <ShoppingCartOutlinedIcon />}
          sx={styles.buyBtn}
        >
          {buying ? 'Opening checkout…' : 'Buy plan'}
        </Button>
      </Box>
    </Paper>
  );
}

function getEmail() {
  return getClientEmail();
}

/** Unwrap plan arrays from GET /user/subscription/plans and /addons/plans responses. */
function extractPlanList(response) {
  const body = response?.data ?? response;
  if (!body) return [];
  const rows = body.data ?? body.plans ?? (Array.isArray(body) ? body : null);
  return Array.isArray(rows) ? rows : [];
}

/** Normalize catalog rows from GET /user/subscription/plans or /addons/plans for PlanCard. */
function normalizeUserCatalogPlan(raw, defaultCategory = 'main') {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw._id ?? raw.id ?? raw.subscriptionPlanId ?? raw.planId;
  if (!id) return null;
  const discountPercent = raw.pricing?.discountPercent ?? raw.pricing?.discount?.percent ?? 0;
  const finalPrice =
    raw.pricing?.finalPrice ??
    raw.pricing?.discount?.finalPrice ??
    raw.price?.finalPrice;
  const hoursPerMonth =
    raw.hoursPerMonth ??
    raw.benefits?.hoursPerMonth ??
    (raw.benefits?.sessionsPerMonth && raw.benefits?.hoursPerSession
      ? raw.benefits.sessionsPerMonth * raw.benefits.hoursPerSession
      : null);

  const basePrice = raw.pricing?.basePrice ?? raw.price?.amount;

  return {
    ...raw,
    _id: String(id),
    planCategory: raw.planCategory || defaultCategory,
    planType: raw.planType ?? null,
    description: raw.description ?? null,
    hoursPerMonth,
    validityMonths: raw.validityMonths ?? null,
    freeMonths: Number(raw.freeMonths) || 0,
    autoPayMonths: raw.autoPayMonths ?? null,
    isAutoPayRequired: Boolean(raw.isAutoPayRequired),
    metadata: raw.metadata || { status: 'active' },
    pricing: {
      basePrice,
      currency: raw.pricing?.currency || raw.price?.currency || 'INR',
      discountPercent,
      finalPrice,
      discount: {
        percent: discountPercent,
        finalPrice,
      },
    },
    billing: raw.billing ?? {
      autoPay: { enabled: false, billingCycle: 'monthly' },
    },
    benefits: {
      sessionsPerMonth: raw.benefits?.sessionsPerMonth ?? null,
      hoursPerSession: raw.benefits?.hoursPerSession ?? null,
      hoursPerMonth,
      unlimitedAccess: Boolean(raw.benefits?.unlimitedAccess),
      features: Array.isArray(raw.benefits?.features) ? raw.benefits.features : [],
    },
    restrictions: raw.restrictions ?? {},
  };
}

/**
 * Main plan catalog — GET /user/subscription/plans (add-ons use Addonplan.js).
 */
function PlanCatalogView({ compact = false, onPaymentSuccess }) {
  const navigate = useNavigate();
  const [mainPlans, setMainPlans] = useState([]);
  const [paymentContext, setPaymentContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const canSelectPlan = paymentContext?.canSelectPlan !== false;
  const contextMessage = paymentContext?.message || '';
  const subscriptionAlreadyActive =
    paymentContext?.paymentFlow === PAYMENT_FLOW_SUBSCRIPTION_ACTIVE ||
    paymentContext?.canAccessRobots;

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const mainRes = await fetchSubscriptionCatalogPlans();

      if (mainRes.data?.success === false) {
        throw new Error(mainRes.data?.message || 'Failed to load subscription plans.');
      }

      const ctx = mainRes.data?.paymentContext ?? null;
      setPaymentContext(ctx);

      const mainList = extractPlanList(mainRes)
        .map((row) => normalizeUserCatalogPlan(row, 'main'))
        .filter(Boolean);
      setMainPlans(mainList);

      if (!compact) {
        if (isContractCheckoutPaymentContext(ctx)) {
          navigate(USER_CONTRACT_CHECKOUT_PATH, { replace: true });
          return;
        }

        if (
          (ctx?.paymentFlow === PAYMENT_FLOW_SUBSCRIPTION_ACTIVE || ctx?.canAccessRobots) &&
          mainList.length === 0
        ) {
          if (onPaymentSuccess) {
            onPaymentSuccess();
          } else {
            navigate(USER_APP_ROBOTS_PATH, { replace: true });
          }
          return;
        }
      }

      if (!mainList.length) {
        setMessage(mainRes.data?.message || 'No subscription plans are available right now.');
      } else if (ctx?.message && ctx.canSelectPlan === false) {
        setMessage(ctx.message);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      if (err.response?.status === 404) {
        setMainPlans([]);
        setMessage(msg || 'No subscription plans found.');
      } else {
        setError(msg || 'Failed to load subscription plans.');
        setMainPlans([]);
      }
    } finally {
      setLoading(false);
    }
  }, [compact, navigate, onPaymentSuccess]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const startPurchase = async (plan) => {
    if (!plan || buyingPlanId || !canSelectPlan) return;
    const email = getEmail();
    if (!email) {
      setSnackbar({ open: true, message: 'Please sign in again to purchase a plan.', severity: 'error' });
      return;
    }

    const planId = plan._id;
    setBuyingPlanId(planId);

    try {
      const initiateRes = await selectSubscriptionPlan({
        subscriptionPlanId: planId,
      });

      const payload = initiateRes.data?.data || initiateRes.data;

      if (payload?.mode === 'pending_admin_review') {
        setSnackbar({
          open: true,
          message:
            payload.message ||
            'Plan request submitted. Admin will create your contract; you will pay after that to activate.',
          severity: 'success',
        });
        await fetchPlans();
        return;
      }

      if (payload?.mode === 'free_month') {
        setSnackbar({
          open: true,
          message:
            payload.message ||
            'Free month recorded. An admin will finalize your contract.',
          severity: 'success',
        });
        onPaymentSuccess?.();
        return;
      }

      if (payload?.mode !== 'razorpay_order') {
        throw new Error(initiateRes.data?.message || 'Payment could not be started.');
      }

      const razorpayKeyId = payload.razorpayKeyId;
      const order = payload.order;
      if (!razorpayKeyId || !order?.id) {
        throw new Error('Payment could not be started. Missing Razorpay order details.');
      }

      const confirmRes = await openRazorpayOrderCheckout({
        razorpayKeyId,
        order,
        customerId: payload.customerId,
        charge: payload.charge,
        description: plan.planName,
        prefill: { email },
        onConfirm: (razorpayPayload) => confirmSubscriptionPlanPayment(razorpayPayload),
      });

      const planRequestStatus = confirmRes?.data?.data?.planRequest?.status;
      const baseMsg =
        confirmRes?.data?.message ||
        (planRequestStatus === 'payment_captured'
          ? `Payment received for ${plan.planName}. Your plan is recorded — contract activation is completed by admin.`
          : `Payment received for ${plan.planName}.`);
      setSnackbar({
        open: true,
        message: baseMsg,
        severity: 'success',
      });
      onPaymentSuccess?.();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      if (data.redirectRoute) {
        navigate(webPathFromAppRedirectRoute(data.redirectRoute));
        return;
      }
      const msg =
        data.message || data.error || err.message || 'Could not complete subscription purchase.';
      if (msg !== 'Payment cancelled') {
        setSnackbar({
          open: true,
          message:
            status === 409
              ? `${msg} Complete checkout on this page or contact support.`
              : msg,
          severity: status === 409 ? 'warning' : 'error',
        });
      }
    } finally {
      setBuyingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: compact ? 200 : 320 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
    <Box sx={compact ? undefined : styles.pageWrap}>
      {!compact && (
        <Typography sx={styles.pageTitle}>Subscription plans</Typography>
      )}
      {!compact && (
        <Typography sx={styles.pageSubtitle}>
          Choose a UV subscription plan. After admin approves your request, complete contract
          payment to activate your subscription.
        </Typography>
      )}
      {!compact && isContractCheckoutPaymentContext(paymentContext) ? (
        <SubscriptionPaymentBanner sx={{ mb: 2 }} />
      ) : null}
      {subscriptionAlreadyActive ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {contextMessage ||
            'Your subscription is active. Browse plans below or open Robots to use the service.'}
        </Alert>
      ) : null}
      {contextMessage && !subscriptionAlreadyActive ? (
        <Alert
          severity={
            paymentContext?.paymentFlow === PAYMENT_FLOW_CONTRACT_PENDING_ADMIN
              ? 'warning'
              : canSelectPlan
                ? 'info'
                : 'warning'
          }
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            isContractCheckoutPaymentContext(paymentContext) ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => navigate(USER_CONTRACT_CHECKOUT_PATH)}
              >
                Pay now
              </Button>
            ) : null
          }
        >
          {contextMessage}
        </Alert>
      ) : null}
      {paymentContext?.openContract?.contractNumber ? (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
          Contract {paymentContext.openContract.contractNumber}
          {paymentContext.openContract.status
            ? ` · ${String(paymentContext.openContract.status).replace(/_/g, ' ')}`
            : ''}
        </Typography>
      ) : null}
      {message && mainPlans.length ? (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
          {message}
        </Typography>
      ) : null}

      {mainPlans.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {mainPlans.map((plan) => (
            <PlanCard
              key={plan._id}
              plan={plan}
              onBuyPlan={startPurchase}
              buying={buyingPlanId === plan._id}
              purchaseDisabled={!canSelectPlan || subscriptionAlreadyActive}
            />
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
          {message || 'No subscription plans are available right now.'}
        </Typography>
      )}
    </Box>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        sx={{ borderRadius: 2, width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
}

/**
 * Routes plan catalog vs contract checkout by URL and backend paymentContext.
 * @param {{ compact?: boolean, robotId?: string, onPaymentSuccess?: () => void }} props
 */
export function PaymentPlanView(props) {
  const { compact = false } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [routing, setRouting] = useState(!compact);

  useEffect(() => {
    if (compact) {
      setRouting(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSubscriptionStatus();
        if (cancelled || !res.data?.success) return;
        const ctx = res.data.paymentContext;
        const target = resolvePathFromPaymentContext(ctx);
        const onCheckout = location.pathname.startsWith(USER_CONTRACT_CHECKOUT_PATH);
        const onRobots = location.pathname.startsWith('/user-app');
        const onPaymentScreen =
          location.pathname.startsWith(USER_SUBSCRIPTION_PLANS_PATH) || onCheckout;

        if (target === USER_APP_ROBOTS_PATH && !onRobots && onPaymentScreen) {
          navigate(USER_APP_ROBOTS_PATH, { replace: true });
          return;
        }
        if (isContractCheckoutPaymentContext(ctx) && !onCheckout) {
          navigate(USER_CONTRACT_CHECKOUT_PATH, { replace: true });
        }
      } catch {
        /* PlanCatalogView / ContractCheckoutView load their own data */
      } finally {
        if (!cancelled) setRouting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [compact, location.pathname, navigate]);

  if (routing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (location.pathname.startsWith(USER_CONTRACT_CHECKOUT_PATH)) {
    return <ContractCheckoutView {...props} />;
  }
  return <PlanCatalogView {...props} />;
}

export default PaymentPlanView;

const styles = {
  pageWrap: {
    maxWidth: 900,
    mx: 'auto',
    py: { xs: 2, md: 3 },
    px: { xs: 2, md: 0 },
  },
  pageTitle: {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
    mb: 0.5,
  },
  pageSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mb: 3,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.colors.textSecondary,
    mb: 1.5,
  },
  planCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: `${theme.radius.lg}px`,
    border: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgPrimary,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    flexWrap: 'wrap',
  },
  planName: {
    fontSize: { xs: '1.1rem', md: '1.25rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
  },
  planDesc: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mt: 0.75,
    lineHeight: 1.5,
  },
  priceBlock: {
    textAlign: 'right',
    flexShrink: 0,
  },
  basePriceStrike: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textDecoration: 'line-through',
  },
  finalPrice: {
    fontSize: { xs: '1.35rem', md: '1.5rem' },
    fontWeight: 800,
    color: accent,
    lineHeight: 1.2,
  },
  discountNote: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.accentSuccess,
    mt: 0.25,
  },
  billingNote: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    mt: 0.25,
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.textPrimary,
  },
  featuresTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
    mb: 1,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
  },
  featureText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 1.4,
  },
  buyRow: {
    mt: 2.5,
    pt: 2,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buyBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    borderRadius: 2,
    px: 3,
    py: 1.1,
    bgcolor: accent,
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    '&:hover': {
      bgcolor: theme.colors.brandPrimary,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
    },
    '&.Mui-disabled': {
      bgcolor: 'rgba(37, 99, 235, 0.35)',
      color: 'rgba(255,255,255,0.9)',
    },
  },
};
